import React from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { useFormContext } from 'react-hook-form';
import { StripeCardElementChangeEvent } from '@stripe/stripe-js';

import { useHasChangedShallow } from '$/hooks/useHasChanged';

import ToggleLeftRight from '../ui/Input/Basics/ToggleLeftRight';
import CountryInput from '../ui/Input/Location/CountryInput';
import ProvinceInput, { provinceMap } from '../ui/Input/Location/ProvinceInput';
import StateInput, { stateMap } from '../ui/Input/Location/StateInput';
import StripeCard from '../ui/Input/StripeCard';
import Creds from '../ui/Creds';
import Form from '../ui/Form';
import Input from '../ui/Input/Basics/Input';
import Wizard from '../ui/wizard/Wizard';
import { BillingCycle } from '$/GraphQL/schema/types';
import { useMeMaybe } from '$/hooks/useMe';

export const BillingAddressAndCC: React.FC<{
  update?: boolean;
  hideCardInput?: boolean;
  showBillingCycleSwitch?: boolean;
  onCardChange?: (event: StripeCardElementChangeEvent) => void;
}> = ({ update, hideCardInput, showBillingCycleSwitch, onCardChange }) => {
  const me = useMeMaybe();
  const { setValue, watch } = useFormContext();
  const country = watch('team.address.country', me?.lastCountry ?? 'US');
  const [provinceState, setProvinceState] = React.useState<{
    ProvinceControl: React.FC<any>;
    provincePlaceholder: string;
    zipcodePlaceholder: string;
  }>({
    ProvinceControl: Input,
    provincePlaceholder: 'Enter Province',
    zipcodePlaceholder: 'Postal code',
  });

  React.useEffect(() => {
    switch (country) {
      case 'US':
        setProvinceState({
          ProvinceControl: StateInput,
          provincePlaceholder: 'State',
          zipcodePlaceholder: 'Zip code',
        });
        break;
      case 'CA':
        setProvinceState({
          ProvinceControl: ProvinceInput,
          provincePlaceholder: 'Province',
          zipcodePlaceholder: 'Postal code',
        });
        break;
      default:
        setProvinceState({
          ProvinceControl: Input,
          provincePlaceholder: 'Province',
          zipcodePlaceholder: 'Postal code',
        });
        break;
    }
  }, [country]);

  const hasProvinceControlChanged = useHasChangedShallow([provinceState.ProvinceControl], {
    skipInitial: true,
  });
  React.useEffect(() => {
    if (!hasProvinceControlChanged) return;
    // All of this is done so auto-complete has a higher chance of working

    const oldValue = watch('team.address.province');
    let newKey = '';

    if (provinceState.ProvinceControl === StateInput) {
      // Try to find the state value from the current value
      for (const key of Object.keys(stateMap)) {
        if (stateMap[key] === oldValue) {
          newKey = key;
          break;
        }
      }
    }

    if (provinceState.ProvinceControl === ProvinceInput) {
      // Try to find the state value from the current value
      for (const key of Object.keys(provinceMap)) {
        if (provinceMap[key] === oldValue) {
          newKey = key;
          break;
        }
      }
    }

    if (provinceState.ProvinceControl === ProvinceInput) {
      newKey = provinceMap[newKey] || '';
    }

    if (newKey) {
      setValue('team.address.province', newKey, { shouldDirty: true });
    }
  }, [country, hasProvinceControlChanged, provinceState.ProvinceControl, setValue, watch]);

  return (
    <>
      {!hideCardInput && (
        <>
          <Wizard.Subtitle className='mx-2'>
            {update ? 'Update payment details' : 'Payment details'}
          </Wizard.Subtitle>
          <div
            className='flex flex-wrap align-center px-2 -mx-2'
            css={css`
              width: calc(100% + 1rem);
            `}
          >
            <Form.Field
              name='firstName'
              component={<Input autoComplete='given-name' placeholder='First name' />}
            />
            <Form.Field
              name='lastName'
              component={<Input autoComplete='family-name' placeholder='Last name' />}
            />

            <Form.Field
              name='card'
              className='w-full'
              onChange={onCardChange}
              renderErrorGap
              component={<StripeCard />}
            />
          </div>
          <Wizard.Text className='w-full md:mt-0 text-center md:text-md'>
            <span className='font-semibold'>
              Your card will not be charged till your trial ends. You may cancel any time.
            </span>
            <br />
            All new subscriptions are monthly. For 15% off, switch to annual.{' '}
            <a target='_blank' rel='noreferrer noopener' href='https://ewebinar.com/pricing'>
              See our pricing
            </a>
          </Wizard.Text>

          {showBillingCycleSwitch && (
            <Form.Field
              name='team.billingCycle'
              className='w-full'
              component={
                <ToggleLeftRight
                  css={css`
                    margin: auto;
                    .right,
                    .left {
                      ${tw`font-bold text-md`}
                    }
                    input:checked + .switch {
                      ${tw`bg-gray-1`}
                    }
                  `}
                  leftValue={BillingCycle.Month}
                  rightValue={BillingCycle.Year}
                  leftLabel='Monthly'
                  rightLabel={<span>Annual (15% off)</span>}
                />
              }
            />
          )}
          <Creds.Separator />
        </>
      )}

      <Wizard.Subtitle className='mx-2'>Billing address</Wizard.Subtitle>
      <div className={`flex flex-wrap w-full`}>
        <Form.RawField name='team.billingCycle' component={<Input type='hidden' />} />
        <Form.Field
          name='team.address.company'
          className='w-full'
          component={<Input autoComplete='company' placeholder='Company' />}
        />
        <Form.Field
          name='team.address.address1'
          className='w-full'
          component={<Input autoComplete='address-line1' placeholder='Address' />}
        />
        <Form.Field
          name='team.address.city'
          component={<Input autoComplete='address-level2' placeholder='City' />}
        />
        <Form.Field
          name='team.address.province'
          component={
            <provinceState.ProvinceControl
              id='stateInput'
              placeholder={provinceState.provincePlaceholder}
              autoComplete='address-level1'
            />
          }
        />
        <Form.Field
          name='team.address.postal'
          component={
            <Input autoComplete='postal-code' placeholder={provinceState.zipcodePlaceholder} />
          }
        />
        <Form.Field
          component={
            <CountryInput
              id='countryInput'
              autoComplete='country-name'
              placeholder='Your country'
            />
          }
          name='team.address.country'
        />
      </div>
    </>
  );
};
